import { ConciergeBell, Receipt, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useUI } from "@/context/UIContext";
import { useOrder } from "@/context/OrderContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { isOnline } from "@/lib/network";

const COOLDOWN = 30000;
const SERVICE_ERROR_KEY = {
  network: "offlineActionBlocked", rate_limited: "orderRateLimited", table_required: "orderTableRequired",
  table_session_required: "tableSessionRequired",
};


// Always-visible, clearly-labeled service actions (no ambiguous icon-only FAB).
export function FloatingActions() {
  const { openWaiter } = useUI();
  const { actions, requestService } = useOrder();
  const { count } = useCart();
  const { t } = useLang();

  const now = Date.now();
  const waiterPending = actions.some((a) => a.type === "waiter" && now - a.at < COOLDOWN);
  const billPending = actions.some((a) => a.type === "bill" && now - a.at < COOLDOWN);

  const handleBill = async () => {
    if (billPending) return;
    if (!isOnline()) {
      toast.error(t("offlineActionBlocked"));
      return;
    }
    const result = await requestService("bill");
    if (!result.ok) {
      toast.error(t(SERVICE_ERROR_KEY[result.reason] || "serviceFailed"));
      return;
    }
    toast.success(t("billRequested"), { description: t("billRequestedDesc"), icon: <Receipt className="h-4 w-4" /> });
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 mx-auto max-w-md">
      <div className="relative" style={{ height: 0 }}>
        <div className={`pointer-events-auto absolute flex flex-col items-end gap-2.5 transition-all duration-300 end-4 ${count > 0 ? "bottom-40" : "bottom-24"}`}>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={openWaiter}
            className="flex items-center gap-2 rounded-full brand-gradient py-2.5 ps-3 pe-4 text-white shadow-float"
            data-testid="call-waiter-btn"
            aria-label={t("callWaiter")}
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-white/20">
              {waiterPending ? <Check className="h-4 w-4" /> : <ConciergeBell className="h-5 w-5" />}
            </span>
            <span className="whitespace-nowrap text-sm font-bold">
              {waiterPending ? t("waiterPending") : t("callWaiter")}
            </span>
          </motion.button>

          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={handleBill}
            className="flex items-center gap-2 rounded-full border border-border bg-card py-2 ps-2.5 pe-4 shadow-float"
            data-testid="request-bill-btn"
            aria-label={t("requestBill")}
          >
            <span className={`grid h-7 w-7 place-items-center rounded-full ${billPending ? "bg-success text-white" : "bg-secondary text-primary"}`}>
              {billPending ? <Check className="h-4 w-4" /> : <Receipt className="h-4 w-4" />}
            </span>
            <span className="whitespace-nowrap text-sm font-bold">
              {billPending ? t("billSent") : t("requestBill")}
            </span>
          </motion.button>
        </div>
      </div>
    </div>
  );
}
